import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import { REHYDRATE } from 'redux-persist';
import { createListenerMiddleware, isAnyOf, PayloadAction } from '@reduxjs/toolkit';

import { KioskType } from 'types/KioskTypes';
import { GateType } from 'modules/gates/types/Gate';
import { DoorsStatus } from 'modules/kiosk/types/KeysSafe';

import { RootState } from 'App/Store';
import { getUrlParam } from 'utils/urlUtils';
import { resetApi } from '../../App/rootReducer';
import dealersApi from 'modules/dealers/service';
import kioskService from 'modules/kiosk/service';
import translationsApi from 'modules/translations/service';
import selfServicesApi from 'modules/selfServices/service';
import { AppStartListening } from 'App/ListenerMiddleware';
import restrictedAccessApi from 'modules/restrictedAccess/service';

import formActions from 'modules/form/actions';
import { setDoorStatus } from 'modules/kiosk/actions';
import { locationChange, push } from 'modules/router/actions';
import { dismissMobilityWarning, logout, setCookies, setCodeReopenVerified, setLoginData } from 'modules/auth/actions';
import { openFlowTypeModal, resetFlowType } from 'modules/selfServices/actions';
import { closeRestrictedAccessModal, continueRestrictedAccessModal, openRestrictedAccessModal } from 'modules/restrictedAccess/actions';

import {
  canCollectAnalyticsData,
  getAdminToken, getLoginData, getPublicToken, getSelectedSelfServiceId, getToken,
} from 'modules/auth/selectors';
import { buildSentryUser } from 'utils/sentryUtils';
import { getKioskId } from 'modules/kiosk/selectors';
import { isOffline as isOfflineSelector } from 'modules/healthCheck/selectors';
import { getDealerId, getDefaultBrandId, getKioskType, isRemoteKiosk } from 'modules/dealers/selectors';
import {
  getSearch, isHomePage as isHomePageSelector, isLoginPage as isLoginPageSelector, isReopenKioskPage,
} from 'modules/router/selectors';

import { getSelectedSelfService, isKioskNotPrepared } from 'modules/selfServices/selectors';
import { getStepsConfig } from 'modules/selfServices/stepsConfigSelectors';
import { getNextStepAfterHome } from 'components/SelfServices/Steps';
import authApi from './service';
import { canSkipAlreadyAnsweredSteps, shouldOpenFlowTypeModal, RedirectPathConfig } from './utils';

import { Token } from './types/Token';
import { MfaScopeEnum } from './types/LoginRequest';
import { LoginMFAResponse } from './types/LoginResponse';
import { LOCAL_STORAGE_KEY } from './types/InitialState';
import { toUrlSlug } from 'utils/stepUrlUtils';

const listenerMiddleware = createListenerMiddleware();

const startAppListening = listenerMiddleware.startListening as AppStartListening;

startAppListening({
  actionCreator: setCookies,
  effect: ({ payload }) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
  },
});

// Keep the Sentry user in sync with the self-service session identity so
// issues report a real impacted-user count instead of "0 users impacted".
startAppListening({
  actionCreator: setLoginData,
  effect: (_, { getState }) => {
    const state = getState() as RootState;
    Sentry.setUser(buildSentryUser({
      publicToken: getPublicToken(state),
      selfServiceId: getSelectedSelfServiceId(state),
      dealerId: getDealerId(state),
    }));
  },
});

startAppListening({
  actionCreator: logout,
  effect: () => {
    Sentry.setUser(null);
  },
});

startAppListening({
  actionCreator: logout,
  effect: async ({ payload, meta }, { dispatch, getOriginalState }) => {
    const { asAdmin } = meta;

    const state = getOriginalState() as RootState;

    const token = getToken(state);
    const kioskId = getKioskId(state);
    const kioskType = getKioskType(state);
    const adminToken = getAdminToken(state);
    const isOffline = isOfflineSelector(state);
    const selfServiceId = getSelectedSelfServiceId(state);

    if (!isOffline) {
      if (asAdmin) {
        dispatch(kioskService.endpoints.closeAllSlotsAsAdmin.initiate({ kioskId, adminToken }));
      } else if (kioskType === KioskType.PARTTEAM && selfServiceId) {
        dispatch(kioskService.endpoints.reset.initiate({
          kioskId, selfServiceId, token, ...payload,
        }));
      }
    }

    dispatch(resetApi());
    dispatch(dealersApi.endpoints.getContext.initiate());
    // Need to reset it manually as closeAllSlotsAsAdmin will put back doors as closed.
    dispatch(setDoorStatus(DoorsStatus.PENDING));
    dispatch(resetFlowType());
    dispatch(translationsApi.endpoints.getLanguages.initiate());

    const pathToken = getUrlParam('token');
    dispatch(push(`/?token=${pathToken}`));
  },
});

// Initialise Login page with default value
startAppListening({
  matcher: isAnyOf(
    dealersApi.endpoints.getContext.matchFulfilled,
    locationChange.match,
  ),
  effect: async (_, { dispatch, getState }) => {
    const isHomePage = isHomePageSelector(getState());
    const isLoginPage = isLoginPageSelector(getState());
    if (isHomePage) {
      dispatch(setLoginData({}));
    } else if (isLoginPage) {
      const state = getState();

      const loginData = getLoginData(state);
      const dealerId = loginData.dealerId ?? getDealerId(state);
      const isDriver = loginData.isDriver ?? true;
      const brandId = getDefaultBrandId(state, dealerId);
      dispatch(setLoginData({
        ...loginData, dealerId, brandId, isDriver,
      }));
    }
  },
});

// Login Qr Code Redirection to Login on error
startAppListening({
  matcher: authApi.endpoints.sendQrCode.matchRejected,
  effect: (action, { dispatch, getState }) => {
    const isHomePage = isHomePageSelector(getState());
    console.log('[auth/middleware] sendQrCode.matchRejected - if home page, redirect to login :', isHomePage );
    if (isHomePage) {
      const search = getSearch(getState());
      dispatch(push(`/login${search}`, { type: action.meta.arg.originalArgs.type }));
    }
  },
});

// Login logic
startAppListening({
  matcher: isAnyOf(
    authApi.endpoints.login.matchFulfilled,
    authApi.endpoints.loginMFA.matchFulfilled,
    authApi.endpoints.sendQrCode.matchFulfilled,
  ),
  effect: async (action, { dispatch, take, getState }) => {
    const canUseGA = canCollectAnalyticsData(getState());
    const { payload } = action as PayloadAction<LoginMFAResponse>;
    const { scope } = jwtDecode<Token>(payload.token);
    const isReopen = isReopenKioskPage(getState() as RootState);
    const isSendQrCode = authApi.endpoints.sendQrCode.matchFulfilled(action);
    const isLoginAction = authApi.endpoints.login.matchFulfilled(action);
    const isLoginMFA = authApi.endpoints.loginMFA.matchFulfilled(action);

    console.log('[auth/middleware] login listener fired', {
      action: isSendQrCode ? 'sendQrCode' : isLoginMFA ? 'loginMFA' : isLoginAction ? 'login' : 'unknown',
      scope,
      isReopen,
      pathname: (getState() as RootState).router?.location?.pathname,
    });

    if (scope === MfaScopeEnum.REOPEN || isReopen) {
      if (isSendQrCode) {
        console.log('[auth/middleware] → reopen branch: dispatching setCodeReopenVerified');
        dispatch(setCodeReopenVerified());
      }
      if (canUseGA) {
        ReactGA.event({
          category: 'Login',
          action: 'Reopen kiosk',
          label: 'Reopen kiosk completed',
        });
      }
    } else {
      console.log('[auth/middleware] → not detected as a reopen flow - : RESETTING FORM', {
        pathname: (getState() as RootState).router?.location?.pathname,
        scope,
        isReopen,
      });
      dispatch(formActions.resetForm());

      const isSimpleLogin = authApi.endpoints.login.matchFulfilled(action)
        || authApi.endpoints.sendQrCode.matchFulfilled(action);

      const selfServiceId = isSimpleLogin
        ? action.payload.selfServiceId
        : getSelectedSelfServiceId(getState()) ?? jwtDecode<Token>(payload.token).self_service_id;

      const { data: selfService, isSuccess } = await dispatch(
        selfServicesApi.endpoints.getSelfServiceById.initiate({ id: selfServiceId }),
      );

      if (isSuccess) {
        const isRemote = isRemoteKiosk(getState() as RootState);
        const dispatchGateEvent = isSimpleLogin && selfService.kioskRestrictedAccess && isRemote;
        const isNotPrepared = isKioskNotPrepared(getState() as RootState);

        // Mobility not prepared warning in code login scope
        if (isSimpleLogin && isNotPrepared) {
          const [resolvedAction] = await take(isAnyOf(dismissMobilityWarning, logout));
          if (logout.match(resolvedAction)) {
            return;
          }
        }

        // Restricted access ("Gate opening")
        if (dispatchGateEvent) {
          // A 'side-effect' (such as gate opening) might happen between those actions
          dispatch(openRestrictedAccessModal());
          await take(closeRestrictedAccessModal.match);
        }

        // Quick/Classic check-in
        if (shouldOpenFlowTypeModal(selfService)) {
          dispatch(openFlowTypeModal());
        } else {
          const search = getSearch(getState());
          const stepsConfig = getStepsConfig(getState());
          const canSkip = canSkipAlreadyAnsweredSteps(selfService);
          const { type }= selfService;
          const nextStep = getNextStepAfterHome(selfService, stepsConfig);

          if (canSkip && nextStep) {
            console.log('[auth/middleware] redirect → canSkip, next step:', nextStep, `${RedirectPathConfig[type]}/${toUrlSlug(nextStep)}${search}`);
            dispatch(push(`${RedirectPathConfig[type]}/${toUrlSlug(nextStep)}${search}`, { allowReviewCheckIn: true } ));
          } else {
            console.log('[auth/middleware] redirect → no skip, form start:', `${RedirectPathConfig[type]}${search}`);
            dispatch(push(`${RedirectPathConfig[type]}${search}`));
          }

        }

        if (canUseGA) {
          ReactGA.event({
            category: 'Login',
            action: `${selfService.origin} ${selfService.type} login`,
            label: 'Login completed',
          });
        }
      }
    }
  },
});

// 'Side-effects' handling regarding restricted access
startAppListening({
  actionCreator: continueRestrictedAccessModal,
  effect: async (_, { dispatch, getState }) => {
    const selfService = getSelectedSelfService(getState());
    if (selfService.kioskRestrictedAccess.type === GateType.RELAY) {
      dispatch(restrictedAccessApi.endpoints.open.initiate({ selfServiceId: selfService.id }));
    }
  },
});

// Close restricted access modal after side effects
startAppListening({
  matcher: restrictedAccessApi.endpoints.open.matchFulfilled,
  effect: async (_, { dispatch }) => {
    dispatch(closeRestrictedAccessModal());
  },
});

// Reset login data when login page is reloaded.
startAppListening({
  type: REHYDRATE,
  effect: async (_, { dispatch, getState }) => {
    const currentLoginData = getLoginData(getState());
    dispatch(setLoginData({ ...currentLoginData, locationId: undefined }));
  },
});

export default listenerMiddleware;
